import { type ReactElement, useEffect, useRef } from "react";
import { WebsocketContext } from "./WebsocketContext";
import useAuth from "../../features/auth/hooks/useAuth";
import type {
  DeleteMessageEvent,
  IsTypingEvent,
  NewMessagesEvent,
  ReadMessagesEvent,
  UpdateMessageEvent,
  WSUserOfflineEvent,
  WSUserOnlineEvent,
} from "../../features/conversations/ts";
import useConversations from "../../features/conversations/hooks/useConversations";
import { useProfile } from "../../features/account_settings/hooks/useProfile";
import { useAppDispatch } from "../../store/store";
import { commonActions } from "../../features/common/redux/slice";
import useCommon from "../../features/common/hooks/useCommon";
// import useConversations from "../../features/conversations/hooks/useConversations";
// import type {
//   WSUserOfflineEvent,
//   WSUserOnlineEvent,
// } from "../../features/conversations/ts";

const WS_URL = import.meta.env.VITE_WS_URL;
type WsStatus = "online" | "offline" | "idle";
export const WebsocketProvider = ({
  children,
}: {
  children: ReactElement;
}): ReactElement => {
  const wsRef = useRef<WebSocket | null>(null);
  const { accessToken } = useAuth();
  const {
    addNewMessages,
    setIsTyping,
    updateMessages,
    deleteMessages,
    setMemberStatus,
    readMessages,
    setTotalUnreadMessageCount,
  } = useConversations();
  const { userProfile, setStatus } = useProfile();
  const IDLE_TIME = 60 * 1000;
  const dispatch = useAppDispatch();
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // const currentStatus = useRef<WsStatus>("idle");
  const statusRef = useRef<WsStatus>("online");
  // const activeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { ws_status } = useCommon();
  const activityTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const is_socket_features_enabled =
    (
      import.meta.env.VITE_ENABLED_FULL_SOCKET_FEATURES as string
    ).toLocaleLowerCase() === "true";

  // const { setMemberStatus } = useConversations();
  // const { setStatus } = useUserProfileData()

  // const onUserStatusChange = (
  //   data: WSUserOnlineEvent | WSUserOfflineEvent,
  //   is_online: boolean,
  // ) => {
  //   const payload = { user_id: data.user_id, is_online };
  //   setMemberStatus(payload);
  //   // setStatus(payload)
  // };

  const setNextStatus = (next: WsStatus) => {
    // if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    // if (currentStatus.current !== status) {
    //   currentStatus.current = status;

    if (ws_status === "offline") return;

    if (statusRef.current === next) return;

    statusRef.current = next;

    if (is_socket_features_enabled) {
      wsRef.current?.send(
        JSON.stringify({
          action: "presence",
          status: next,
        }),
      );
    }

    dispatch(commonActions.setWsStatus(next));
  };

  // const setIdle = () => {
  //   if (ws_status === "offline") return;

  //   sendStatus("idle");
  //   dispatch(commonActions.setWsStatus("idle"));
  // };

  // const setOnline = () => {
  //   if (ws_status === "offline") return;

  //   sendStatus("online");
  //   dispatch(commonActions.setWsStatus("online"));
  // };

  const onNewMessages = (data: NewMessagesEvent) => {
    const firstMessage = data.messages[0];
    if (
      (firstMessage && firstMessage.author?.id !== userProfile?.id) ||
      !firstMessage.author
    ) {
      addNewMessages(data);
    }
  };

  const onUpdateMessages = (data: UpdateMessageEvent) => {
    updateMessages(data);
  };

  const onDeleteMessages = (data: DeleteMessageEvent) => {
    deleteMessages(data);
  };

  const onTypingEvent = (data: IsTypingEvent) => {
    setIsTyping(data);
  };

  const onUserStatusChange = (
    data: WSUserOnlineEvent | WSUserOfflineEvent,
    is_online: boolean,
  ) => {
    const payload = { profile_id: data.profile_id, is_online };
    setMemberStatus(payload);
    setStatus(payload);
  };

  const onReadMessagesEvent = (data: ReadMessagesEvent) => {
    readMessages(data);
  };

  const resetTimer = () => {
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
    }

    idleTimer.current = setTimeout(() => {
      setNextStatus("idle");
    }, IDLE_TIME);
  };

  const handleActivity = () => {
    if (activityTimeout.current) {
      clearTimeout(activityTimeout.current);
    }

    activityTimeout.current = setTimeout(() => {
      setNextStatus("online");
      resetTimer();
    }, 1000);
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      setNextStatus("idle");

      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
      }
    } else {
      setNextStatus("online");
      resetTimer();
    }
  };

  useEffect(() => {
    if (!accessToken) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }

    if (wsRef.current) {
      return;
    }
    const socket = new WebSocket(`${WS_URL}?token=${accessToken}`);
    wsRef.current = socket;
    socket.onopen = () => {
      console.log("WebSocket connected");
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log("Websocket message received", data.action);

      switch (data.action) {
        case "online":
          onUserStatusChange(data as WSUserOnlineEvent, true);
          break;
        case "offline":
          onUserStatusChange(data as WSUserOfflineEvent, false);
          break;
        case "typing":
          onTypingEvent(data);
          break;
        case "read_messages":
          onReadMessagesEvent(data);
          break;
        case "chat_messages":
          onNewMessages(data);
          break;
        case "update_messages":
          onUpdateMessages(data);
          break;
        case "delete_messages":
          onDeleteMessages(data);
          break;
        case "count_messages":
          setTotalUnreadMessageCount(data);
          break;
        default:
          console.warn("Unknown websocket message action", data.action);
      }
    };

    socket.onclose = () => {
      console.log("WebSocket disconnected");
    };

    socket.onerror = (err) => {
      console.error("WebSocket error", err);
    };

    const closeSocket = () => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.close(1000, "cleanup");
      }
    };

    window.addEventListener("beforeunload", closeSocket);

    return () => {
      window.removeEventListener("beforeunload", closeSocket);
      closeSocket();
    };
  }, [accessToken]);

  useEffect(() => {
    if (ws_status === "offline") return;

    document.addEventListener("visibilitychange", handleVisibilityChange);

    window.addEventListener("mousemove", handleActivity, { passive: true });
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("click", handleActivity);

    resetTimer();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleActivity);

      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (activityTimeout.current) clearTimeout(activityTimeout.current);
    };
  }, [ws_status]);

  return (
    <WebsocketContext.Provider value={wsRef.current}>
      {children}
    </WebsocketContext.Provider>
  );
};
export { WebsocketContext };
